import { Container, Sprite, type Texture } from 'pixi.js';
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from '@/core/viewport';
import type { FurnitureSpriteKey } from '@/sprites/furniture';
import { TILE_SIZE } from '@/sprites/tiles';
import { FURNITURE, type FurnitureLayer } from '@/world/furniture';
import type { Livestock } from '@/world/livestock';

/**
 * Furniture, animals and the feed trough.
 *
 * Sorted by Y so a chicken in front of a table draws over it and one behind
 * draws under. Without that, the scene reads flat the moment anything moves
 * past anything else.
 */
export class PropsRenderer {
  readonly container = new Container();

  private readonly pool: Sprite[] = [];
  /** Peck animation is time-driven, not per-animal state. */
  private elapsed = 0;

  constructor(private readonly textures: Readonly<Record<FurnitureSpriteKey, Texture>>) {}

  render(options: {
    cameraX: number;
    cameraY: number;
    furniture: FurnitureLayer;
    livestock: Livestock | null;
    trough?: { x: number; y: number } | undefined;
    dt: number;
  }): void {
    const { cameraX, cameraY, furniture, livestock, trough, dt } = options;
    this.elapsed += dt;

    const minX = cameraX - TILE_SIZE;
    const minY = cameraY - TILE_SIZE;
    const maxX = cameraX + VIRTUAL_WIDTH;
    const maxY = cameraY + VIRTUAL_HEIGHT;

    const draws: { key: FurnitureSpriteKey; x: number; y: number }[] = [];

    if (trough) {
      draws.push({ key: 'trough', x: trough.x * TILE_SIZE, y: trough.y * TILE_SIZE });
    }

    for (const piece of furniture.entries()) {
      draws.push({
        key: FURNITURE[piece.id].sprite,
        x: piece.x * TILE_SIZE,
        y: piece.y * TILE_SIZE,
      });
    }

    for (const animal of livestock?.animals ?? []) {
      // Offset the cycle per animal so a flock does not peck in unison.
      const phase = this.elapsed + animal.x * 0.05;
      draws.push({
        key: Math.floor(phase) % 3 === 0 ? 'chickenPeck' : 'chicken',
        x: animal.x,
        y: animal.y,
      });
      if (animal.hasProduce) {
        draws.push({ key: 'egg', x: animal.x, y: animal.y + 4 });
      }
    }

    // Painter's order, then cull. Sorting first keeps the comparison stable
    // regardless of what happens to be on screen.
    draws.sort((a, b) => a.y - b.y);

    let used = 0;
    for (const draw of draws) {
      if (draw.x < minX || draw.x > maxX || draw.y < minY || draw.y > maxY) continue;
      const sprite = this.spriteAt(used++);
      sprite.texture = this.textures[draw.key];
      sprite.position.set(Math.round(draw.x), Math.round(draw.y));
      sprite.visible = true;
    }

    for (let i = used; i < this.pool.length; i++) this.pool[i]!.visible = false;
  }

  destroy(): void {
    this.container.destroy({ children: true });
    this.pool.length = 0;
  }

  private spriteAt(index: number): Sprite {
    let sprite = this.pool[index];
    if (!sprite) {
      sprite = new Sprite();
      this.pool.push(sprite);
      this.container.addChild(sprite);
    }
    return sprite;
  }
}
